import * as React from 'react';
import { useState, useEffect } from 'react';

import { connect } from 'react-redux';
import * as selectors from '../../selectors/selectors';
import { ValgtState, RootState } from '@/state';
import { Hva } from './hva/Hva';
import Hvor from './Hvor';
import { Search } from './Search';
import ValgtContainer from './valgt/ValgtContainer';
import { Vegnett } from './vegnett/Vegnett';
import { DatakatalogContainer } from './datakatalog/DatakatalogContainer';
import { DatakatalogMode } from './datakatalog/constants';
import {
    createDatakatalogTabUrl,
    createDatakatalogWindowHandle,
    DATAKATALOG_WINDOW_FEATURES,
    type DatakatalogWindowHandle,
    type DatakatalogWindowLauncher,
    isVSCodeIntegratedBrowser,
} from './datakatalog/standaloneChannel';
import RightPanel from './RightPanel';

import '@/stylesheets/5_components/limiter/datakatalogOverlay.less';

interface AvgrensningProps {
    filterPanelOpen: boolean;
    searchPanelOpen: boolean;
    selectedFeature: ValgtState;
    isCategorizing: boolean;
    datakatalogDefaultMode: DatakatalogMode;
    datakatalogUrl: string;
}

const mapStateToProps = (state: RootState) => {
    return {
        filterPanelOpen: selectors.stateFilterPanelOpen(state),
        isCategorizing: selectors.stateIsCategorizing(state),
        searchPanelOpen: selectors.stateSearchPanelOpen(state),
        selectedFeature: selectors.stateValgt(state),
        datakatalogDefaultMode: selectors.statePreferences(state)
            .datakatalogDefaultMode as DatakatalogMode,
        datakatalogUrl: selectors.stateConfig(state).datakatalogen_url,
    };
};

const Avgrensning = (props: AvgrensningProps) => {
    const [datakatalogOpen, setDatakatalogOpen] = useState(false);
    const [datakatalogShouldReset, setDatakatalogShouldReset] = useState(false);
    const [datakatalogMode, setDatakatalogMode] = useState<DatakatalogMode>(
        props.datakatalogDefaultMode
    );
    const [tabToken, setTabToken] = useState<string | null>(null);
    const [nextTabToken, setNextTabToken] = useState(() => crypto.randomUUID());
    const [tabReady, setTabReady] = useState(false);
    const [datakatalogWindow, setDatakatalogWindow] =
        useState<DatakatalogWindowHandle | null>(null);
    const inVSCode = isVSCodeIntegratedBrowser(navigator.userAgent);
    const datakatalogTabUrl = createDatakatalogTabUrl(
        window.location.href,
        nextTabToken,
        props.datakatalogUrl
    );
    const standaloneTab: DatakatalogWindowLauncher = {
        url: datakatalogTabUrl,
        asLink: inVSCode,
        label: inVSCode ? 'Åpne i ny VS Code-fane' : 'Åpne i eget vindu',
        pendingLabel: inVSCode ? 'Åpner i ny VS Code-fane' : 'Åpner i eget vindu',
        onOpen: () => {
            if (!inVSCode) {
                const opened = window.open(
                    datakatalogTabUrl,
                    `datakatalog-${nextTabToken}`,
                    DATAKATALOG_WINDOW_FEATURES
                );
                if (!opened) {
                    console.warn(
                        'Kunne ikke åpne Datakatalogen i eget vindu; viser den i Vegkart.'
                    );
                    setDatakatalogWindow(null);
                    setTabToken(null);
                    setTabReady(false);
                    setDatakatalogMode(DatakatalogMode.BIG);
                    setDatakatalogOpen(true);
                    return;
                }
                setDatakatalogWindow(createDatakatalogWindowHandle(opened));
            }
            setTabReady(false);
            setTabToken(nextTabToken);
            setNextTabToken(crypto.randomUUID());
            setDatakatalogMode(DatakatalogMode.FLOATING_WINDOW);
            setDatakatalogOpen(true);
        },
    };
    const moveBelowViewOnMobile =
        !props.searchPanelOpen &&
        !props.filterPanelOpen &&
        props.selectedFeature.isEmpty();

    const handleDatakatalogOpen = (open: boolean) => {
        setDatakatalogOpen(open);
        if (!open) setDatakatalogShouldReset(true);
    };

    // Lukk Datakatalog-vinduet når Vegkart lukkes eller lastes på nytt
    useEffect(() => {
        const closeDatakatalogWindow = () => datakatalogWindow?.close();

        window.addEventListener('beforeunload', closeDatakatalogWindow);
        window.addEventListener('unload', closeDatakatalogWindow);

        return () => {
            window.removeEventListener('beforeunload', closeDatakatalogWindow);
            window.removeEventListener('unload', closeDatakatalogWindow);
            closeDatakatalogWindow();
        };
    }, [datakatalogWindow]);

    return (
        <>
            <RightPanel
                isExtended={props.isCategorizing}
                moveBelowViewOnMobile={moveBelowViewOnMobile}
            >
                <Search
                    datakatalogOpen={datakatalogOpen}
                    datakatalogMode={datakatalogMode}
                    setDatakatalogMode={setDatakatalogMode}
                    setDatakatalogOpen={handleDatakatalogOpen}
                    standaloneTab={
                        datakatalogMode === DatakatalogMode.FLOATING_WINDOW
                            ? standaloneTab
                            : undefined
                    }
                />
                <Vegnett />
                <Hvor />
                <Hva />
                <ValgtContainer />
            </RightPanel>
            <DatakatalogContainer
                open={datakatalogOpen}
                shouldReset={datakatalogShouldReset}
                setOpen={setDatakatalogOpen}
                setShouldReset={setDatakatalogShouldReset}
                mode={datakatalogMode}
                setMode={setDatakatalogMode}
                tabWindow={datakatalogWindow}
                setTabWindow={setDatakatalogWindow}
                tabToken={tabToken}
                setTabToken={setTabToken}
                tabReady={tabReady}
                setTabReady={setTabReady}
                standaloneTab={standaloneTab}
            />
        </>
    );
};

export default connect(mapStateToProps)(Avgrensning);
